import { storableError } from '../../util/errors';
import { getConversations } from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { deletedListing, deletedUser, denormalisedResponseEntities } from '../../util/data';
import { currentUserSelector } from '../../ducks/user.duck';
import {
  addMarketplaceEntities,
  getListingsById,
  getUsersById,
} from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //

export const FETCH_CONVERSATIONS_REQUEST = 'app/ConversationsPage/FETCH_CONVERSATIONS_REQUEST';
export const FETCH_CONVERSATIONS_SUCCESS = 'app/ConversationsPage/FETCH_CONVERSATIONS_SUCCESS';
export const FETCH_CONVERSATIONS_ERROR = 'app/ConversationsPage/FETCH_CONVERSATIONS_ERROR';

export const FETCH_CONVERSATION_COUNT = 'app/ConversationsPage/FETCH_CONVERSATION_COUNT';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchConversationsError: null,
  pagination: null,
  conversations: [],
  totalCount: 0,
};

export default function conversationsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_CONVERSATIONS_REQUEST:
      return { ...state, fetchInProgress: true, fetchConversationsError: null };
    case FETCH_CONVERSATIONS_SUCCESS: {
      return {
        ...state,
        fetchInProgress: false,
        conversations: payload.data,
        pagination: payload.meta,
        totalCount: payload.meta.totalItems,
      };
    }
    case FETCH_CONVERSATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchConversationsError: payload };

    case FETCH_CONVERSATION_COUNT:
      return { ...state, totalCount: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchConversationsRequest = () => ({
  type: FETCH_CONVERSATIONS_REQUEST,
});

const fetchConversationsSuccess = response => ({
  type: FETCH_CONVERSATIONS_SUCCESS,
  payload: response,
});

const fetchConversationsError = e => ({
  type: FETCH_CONVERSATIONS_ERROR,
  error: true,
  payload: e,
});

export const fetchConversationCount = count => ({
  type: FETCH_CONVERSATION_COUNT,
  payload: count,
});

// ================ Selectors ================ //

export const conversationsWithRelationsSelector = state => {
  const currentUser = currentUserSelector(state);
  const { conversations } = state.ConversationsPage;

  return conversations.map(conversation => {
    const { productId, userId, recipientId } = conversation;

    const isProvider = currentUser.id.uuid === recipientId;
    const otherUserId = isProvider ? userId : recipientId;

    const [listing] = getListingsById(state, [{ uuid: productId }]);
    const [otherUser] = getUsersById(state, [{ uuid: otherUserId }]);

    return {
      ...conversation,
      otherUser,
      listing,
    };
  });
};

// ================ Thunks ================ //

const fetchUser = async (sdk, id, params) => {
  try {
    return await sdk.users.show({ id, ...params });
  } catch (e) {
    if (e.status === 404) {
      return { data: { data: deletedUser(id) } };
    }

    return null;
  }
};

const fetchListing = async (sdk, id, params) => {
  try {
    return await sdk.listings.show({ id, ...params });
  } catch (e) {
    if (e.status === 404) {
      return { data: { data: deletedListing(id) } };
    }

    return null;
  }
};

export const fetchTransactionForConversation = conversation => async (dispatch, getState, sdk) => {
  const currentUser = currentUserSelector(getState());
  const isCustomer = currentUser.id.uuid === conversation.userId;

  const response = await sdk.transactions.query({
    listingId: conversation.productId,
    ...(isCustomer
      ? { only: 'order', userId: conversation.recipientId }
      : { only: 'sale', userId: conversation.userId }),
    page: 1,
    perPage: 1,
    'fields.transaction': [],
    'fields.user': [],
    include: ['customer'],
  });

  const [transaction] = denormalisedResponseEntities(response);

  return transaction;
};

export const fetchConversationsRelations = (conversations, listingParams, userParams) => async (
  dispatch,
  getState,
  sdk
) => {
  const currentUser = currentUserSelector(getState());

  await Promise.all(
    conversations.map(async ({ productId, userId, recipientId }) => {
      const isProvider = currentUser.id.uuid === recipientId;
      const otherUserId = isProvider ? userId : recipientId;

      const responses = await Promise.all([
        fetchListing(sdk, productId, listingParams),
        fetchUser(sdk, otherUserId, userParams),
      ]);

      responses.forEach(response => dispatch(addMarketplaceEntities(response)));
    })
  );
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  dispatch(fetchConversationsRequest());

  try {
    const { page = 1 } = parse(search);
    const { data, meta } = await getConversations({ page });

    await dispatch(
      fetchConversationsRelations(
        data,
        {
          'fields.listing': ['title'],
        },
        {
          include: ['profileImage'],
          'fields.image': ['variants.square-small2x'],
        }
      )
    );

    dispatch(fetchConversationsSuccess({ data, meta }));
  } catch (e) {
    dispatch(fetchConversationsError(storableError(e)));
  }
};
